import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';

interface IntervalRenderState {
    intervalTimeStamp: DateTime;
}

function intervalRenderer<P>(
    shouldRenderNewInterval: (lastIntervalTimestamp: DateTime) => boolean,
    Comp: React.ComponentType<IntervalRenderState & P>,
    interval = 16,
): React.ComponentType<P> {
    function IntervalRenderer(props: P) {
        const [intervalTimeStamp, setIntervalTimeStamp] = useState(() =>
            DateTime.local(),
        );

        useEffect(() => {
            const intervalId = setInterval(() => {
                // The updater form sees the current timestamp without the
                // interval having to be torn down and rebuilt on every tick,
                // and returning it unchanged makes React skip the re-render -
                // which is what the class achieved by not calling setState.
                setIntervalTimeStamp(lastIntervalTimeStamp =>
                    shouldRenderNewInterval(lastIntervalTimeStamp)
                        ? DateTime.local()
                        : lastIntervalTimeStamp,
                );
            }, interval);
            return () => clearInterval(intervalId);
        }, []);

        const state: IntervalRenderState = { intervalTimeStamp };
        return <Comp {...state} {...props} />;
    }
    return IntervalRenderer;
}

export function everyNewMinuteIntervalRenderer<P>(
    Comp: React.ComponentType<IntervalRenderState & P>,
) {
    return intervalRenderer((lastIntervalTimestamp: DateTime) => {
        return (
            !lastIntervalTimestamp.hasSame(DateTime.local(), 'minute') ||
            !lastIntervalTimestamp.hasSame(DateTime.local(), 'hour')
        );
    }, Comp);
}
